/**
 * 🔄 Role Conflict Resolution Modal
 * 
 * Handles conflicts when assigning unique roles (Admin, Leader, Leader Alt)
 * that are already occupied by another user in the CMT.
 */

import React, { useState } from 'react';
import { 
  Modal, 
  Radio, 
  Space, 
  Alert, 
  Button, 
  Typography, 
  Divider,
  Tag,
  Avatar
} from 'antd';
import { 
  ExclamationCircleOutlined, 
  UserOutlined, 
  SwapOutlined,
  DeleteOutlined 
} from '@ant-design/icons';

const { Title, Text } = Typography;

const RoleConflictModal = ({ 
  visible,
  onCancel,
  onResolve,
  cmtName,
  targetRole, 
  currentHolder, 
  newUserEmail,
  newUserName,
  loading = false
}) => {
  const [resolutionMethod, setResolutionMethod] = useState(null);

  const resolutionOptions = [
    {
      value: 'transfer_demote_member',
      label: `Transfer role & demote ${currentHolder?.fullName} to Member`,
      description: `${currentHolder?.fullName} will become a regular Member with basic CMT access`,
      icon: <SwapOutlined style={{ color: '#1890ff' }} />,
      type: 'transfer'
    },
    {
      value: 'transfer_demote_observer',
      label: `Transfer role & demote ${currentHolder?.fullName} to Observer`,
      description: `${currentHolder?.fullName} will become an Observer with read-only access`,
      icon: <SwapOutlined style={{ color: '#52c41a' }} />,
      type: 'transfer'
    },
    {
      value: 'transfer_remove',
      label: `Transfer role & remove ${currentHolder?.fullName} from CMT`,
      description: `${currentHolder?.fullName} will be completely removed from this CMT`,
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      type: 'remove',
      danger: true
    },
    {
      value: 'cancel',
      label: 'Cancel invitation',
      description: `Keep ${currentHolder?.fullName} in ${targetRole} role and don't invite ${newUserName}`,
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      type: 'cancel'
    }
  ];

  const handleResolve = () => {
    if (resolutionMethod && onResolve) {
      onResolve(resolutionMethod);
    }
  };

  const getAlertType = () => {
    switch (resolutionMethod?.split('_')[0]) {
      case 'transfer': return 'info';
      case 'remove': return 'error';
      case 'cancel': return 'warning';
      default: return 'warning';
    }
  };

  const getAlertMessage = () => {
    if (!resolutionMethod) return null;
    
    const selectedOption = resolutionOptions.find(opt => opt.value === resolutionMethod);
    return selectedOption?.description;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
          <span>Role Conflict Resolution Required</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button 
          key="resolve" 
          type="primary" 
          onClick={handleResolve}
          disabled={!resolutionMethod || loading}
          loading={loading}
          danger={resolutionMethod === 'transfer_remove'}
        >
          {resolutionMethod === 'cancel' ? 'Cancel Invitation' : 'Proceed with Transfer'}
        </Button>
      ]}
    >
      {/* Conflict Summary */}
      <Alert
        type="warning"
        message={`${targetRole} Role Already Occupied`}
        description={
          <div style={{ marginTop: 8 }}>
            <Text>
              You're trying to assign <strong>{newUserName}</strong> ({newUserEmail}) 
              as <strong>{targetRole}</strong> in <strong>{cmtName}</strong>.
            </Text>
            <br />
            <Text>
              However, <strong>{currentHolder?.fullName}</strong> is currently the {targetRole}.
            </Text>
          </div>
        }
        style={{ marginBottom: 24 }}
      />

      {/* Current vs New User Comparison */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>User Comparison</Title>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'space-around' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ marginBottom: 8 }}>
              <Avatar icon={<UserOutlined />} size={48} />
            </div>
            <Text strong>Current {targetRole}</Text>
            <br />
            <Text>{currentHolder?.fullName}</Text>
            <br />
            <Text type="secondary">{currentHolder?.email}</Text>
            <br />
            <Tag color="blue">Current</Tag>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 0 
          }}>
            <SwapOutlined style={{ fontSize: 24, color: '#d9d9d9' }} />
          </div>
          
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ marginBottom: 8 }}>
              <Avatar icon={<UserOutlined />} size={48} style={{ backgroundColor: '#52c41a' }} />
            </div>
            <Text strong>New {targetRole}</Text>
            <br />
            <Text>{newUserName}</Text>
            <br />
            <Text type="secondary">{newUserEmail}</Text>
            <br />
            <Tag color="green">Incoming</Tag>
          </div>
        </div>
      </div>

      <Divider />

      {/* Resolution Options */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Choose Resolution Method</Title>
        <Text type="secondary">
          Select how you want to handle this role conflict:
        </Text>
      </div>

      <Radio.Group
        value={resolutionMethod}
        onChange={e => setResolutionMethod(e.target.value)}
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {resolutionOptions.map(option => (
            <Radio 
              key={option.value} 
              value={option.value}
              style={{ 
                padding: '12px',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
                margin: '4px 0',
                display: 'block',
                backgroundColor: resolutionMethod === option.value ? '#f6ffed' : '#fafafa'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                {option.icon}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: option.danger ? 'bold' : 'normal', 
                    color: option.danger ? '#ff4d4f' : 'inherit',
                    marginBottom: 4
                  }}>
                    {option.label}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    lineHeight: '1.4' 
                  }}>
                    {option.description}
                  </div>
                </div>
              </div>
            </Radio>
          ))}
        </Space>
      </Radio.Group>

      {/* Selected Resolution Preview */}
      {resolutionMethod && (
        <div style={{ marginTop: 16 }}>
          <Alert
            type={getAlertType()}
            message={`Selected: ${resolutionOptions.find(opt => opt.value === resolutionMethod)?.label}`}
            description={getAlertMessage()}
            showIcon
          />
        </div>
      )}

      {/* Warning for Remove Option */}
      {resolutionMethod === 'transfer_remove' && (
        <Alert
          type="error"
          message="⚠️ Permanent Action Warning"
          description={
            <div>
              <Text strong>{currentHolder?.fullName}</Text> will lose all access to this CMT 
              and will need to be re-invited if you want to add them back later.
              <br />
              <Text type="secondary">
                Consider demoting to Member or Observer instead if they should retain some access.
              </Text>
            </div>
          }
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default RoleConflictModal;
